import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { DemandEmailsButton } from '../../components/DemandEmailsDialog'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Caption1,
  Dropdown,
  Input,
  Option,
  Spinner,
} from '@fluentui/react-components'
import {
  ArrowSort16Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Comment16Regular,
  Delete16Regular,
  Edit16Regular,
  Flowchart16Regular,
  PeopleTeam16Regular,
  Search16Regular,
  Send16Regular,
  Warning16Regular,
} from '@fluentui/react-icons'
import { useDemandHistory, useDemandFieldAudit, useDemandEngagement } from '../../hooks/useDemands'
import { useComments, useCreateComment, useDeleteComment } from '../../hooks/useComments'
import { useEffectiveUser } from '../../hooks/useEffectiveUser'
import { useMentionableUsers } from '../../hooks/useMentionableUsers'
import { MentionTextarea } from '../../components/MentionTextarea'
import { UserAvatar } from '../../components/UserAvatar'
import { useToast } from '../../components/ToastProvider'
// Comments render through the same Markdown pipeline as tickets ON PURPOSE —
// TicketMarkdown's own header explains why there must be exactly one renderer.
import { TicketMarkdown } from '../../components/TicketMarkdown'
import type {
  AcknowledgedWarning,
  Demand,
  DemandStageHistoryEntry,
  DemandFieldAuditEntry,
  EngagementEvent,
} from '../../types/demand'
import type { Comment } from '../../types/comment'
import { STAGE_LABEL } from '../../components/TransitionDialog'
import { bayerColors, semanticWarning } from '../../theme'
import { apiErrorMessage } from '../../lib/apiError'
import { activatable } from '../../lib/activatable'
import { formatRelative } from '../../lib/relativeTime'

const useStyles = makeStyles({
  section: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingVerticalL,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: 0,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 10px',
    borderRadius: '999px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    fontSize: '12px',
    cursor: 'pointer',
    ':hover': { background: tokens.colorNeutralBackground2 },
  },
  chipActive: {
    background: bayerColors.navyLight,
    border: `1px solid ${bayerColors.navyMid}`,
    color: bayerColors.navy,
    fontWeight: 600,
  },
  chipCount: { fontSize: '11px', opacity: 0.7 },
  search: { minWidth: '160px' },
  personFilter: { minWidth: '140px' },
  timeLink: {
    color: 'inherit',
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  targetEntry: {
    boxShadow: `0 0 0 2px ${bayerColors.navyMid}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  // The stream: comment cards and compact event rows share one vertical rail.
  // The rail is a ::before line; everything sitting on it (icon badges, date
  // pills, avatars, cards) has an opaque background to read as "on the line".
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    position: 'relative',
    zIndex: 0,
    '::before': {
      content: '""',
      position: 'absolute',
      left: '13px',
      top: '8px',
      bottom: '8px',
      width: '2px',
      background: tokens.colorNeutralStroke3,
      zIndex: -1,
    },
  },
  dateHeader: {
    width: 'fit-content',
    marginLeft: '1px',
    padding: '1px 10px',
    borderRadius: '999px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  eventItem: {
    display: 'grid',
    gridTemplateColumns: '28px 1fr',
    gap: tokens.spacingHorizontalS,
    alignItems: 'start',
  },
  eventLine: {
    fontSize: '12.5px',
    color: tokens.colorNeutralForeground2,
    paddingTop: '5px',
    overflowWrap: 'anywhere',
  },
  eventBody: { whiteSpace: 'pre-wrap' },
  commentCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    background: tokens.colorNeutralBackground1,
    padding: tokens.spacingVerticalM,
    boxShadow: tokens.shadow2,
  },
  commentHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  commentAuthor: { fontWeight: 600, fontSize: '13px', color: tokens.colorNeutralForeground1 },
  commentBody: { fontSize: '13px', color: tokens.colorNeutralForeground1 },
  iconBadge: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  who: { fontWeight: 600, color: tokens.colorNeutralForeground1 },
  meta: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  groupToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '6px 4px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    color: tokens.colorNeutralForeground2,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': { background: tokens.colorNeutralBackground2 },
  },
  groupMembers: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingLeft: '24px',
    marginTop: tokens.spacingVerticalS,
    borderLeft: `2px solid ${tokens.colorNeutralStroke3}`,
  },
  warnBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    marginTop: '4px',
    padding: '2px 8px',
    borderRadius: '999px',
    border: `1px solid ${semanticWarning}55`,
    background: `${semanticWarning}14`,
    color: semanticWarning,
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': { background: `${semanticWarning}22` },
  },
  warnList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '4px',
    paddingLeft: '10px',
    borderLeft: `2px solid ${semanticWarning}55`,
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  showMore: { alignSelf: 'flex-start', marginTop: tokens.spacingVerticalXS },
  emptyFiltered: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  composer: {
    marginBottom: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  composerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  key: {
    background: tokens.colorNeutralBackground3,
    padding: '2px 5px',
    borderRadius: '3px',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '11px',
  },
})

export interface TimelineTabProps {
  demand: Demand
}

interface TimelineEntry {
  ts: string
  kind: 'comment' | 'stage' | 'field' | 'engagement'
  who: string
  whoId?: number | null
  body: string
  id: string
  /** Stage entries only: warnings the actor moved past (#988's paper trail). */
  warnings?: AcknowledgedWarning[]
  /** Comment entries only: an admin-removed tombstone. */
  deleted?: boolean
}

type TimelineKind = TimelineEntry['kind']
type FilterKey = 'all' | TimelineKind

function isFilterKey(v: string | null): v is FilterKey {
  return v === 'all' || v === 'comment' || v === 'stage' || v === 'field' || v === 'engagement'
}

const KIND_META: Record<TimelineKind, { label: string; icon: ReactElement; color: string }> = {
  comment: { label: 'Comments', icon: <Comment16Regular />, color: bayerColors.navyMid },
  stage: { label: 'Stage', icon: <Flowchart16Regular />, color: '#8b5cf6' },
  field: { label: 'Field edits', icon: <Edit16Regular />, color: '#9ca3af' },
  engagement: { label: 'People', icon: <PeopleTeam16Regular />, color: '#0891b2' },
}
const KIND_ORDER: TimelineKind[] = ['comment', 'stage', 'field', 'engagement']
const REVEAL_LIMIT = 25

function dateLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Bold `@Name` for names we can vouch for. MentionTextarea inserts mentions as
 * plain `@Full Name` text, so this matches only against the mentionable-user
 * list (longest first — "Ana Torres-Vega" before "Ana Torres") and leaves
 * everything else untouched. A mention of someone since renamed or removed
 * simply stays plain text.
 */
function highlightMentions(body: string, names: string[]): string {
  if (names.length === 0) return body
  const escaped = [...names]
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  return body.replace(new RegExp(`@(${escaped.join('|')})`, 'g'), '**@$1**')
}

type Row =
  | { type: 'entry'; entry: TimelineEntry }
  | { type: 'field-group'; id: string; members: TimelineEntry[] }

/**
 * Demand detail Timeline tab — stage history + comments + field audits +
 * engagement, interleaved chronologically. Comments render as Markdown cards;
 * system events as compact rows on a shared vertical rail, under date headers.
 * Enhancements: filter by event type, free-text search, collapse noisy
 * field-edit runs, sort direction, and progressive reveal for long histories.
 */
export function TimelineTab({ demand }: TimelineTabProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const toast = useToast()
  const { data: history, isLoading: historyLoading, isError: historyError } =
    useDemandHistory(demand.id)
  const { data: comments, isLoading: commentsLoading, isError: commentsError } =
    useComments(demand.id)
  const { data: fieldAudit, isLoading: auditLoading, isError: auditError } =
    useDemandFieldAudit(demand.id)
  const { data: engagement, isLoading: engagementLoading, isError: engagementError } =
    useDemandEngagement(demand.id)
  const { data: mentionableUsers = [] } = useMentionableUsers(demand.focus_area_code)
  const createComment = useCreateComment()
  const deleteComment = useDeleteComment()
  const effectiveUser = useEffectiveUser()
  const isAdmin = effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'
  const loading = historyLoading || commentsLoading || auditLoading || engagementLoading
  // The timeline is four requests merged into one stream, so any one of them
  // failing silently removes a whole category of events — and with all four
  // empty the tab claimed "No activity yet." about a demand with a full
  // history. Partial is still worth showing; it just must not be presented as
  // the whole record.
  const partialFailure = historyError || commentsError || auditError || engagementError

  const [body, setBody] = useState('')
  const [mentionedIds, setMentionedIds] = useState<number[]>([])
  // The URL IS the view state — read every render, never copied into useState.
  //
  // These used to be `useState(() => searchParams.get(…))`, which runs its
  // initialiser once at mount. Nothing re-read the params afterwards, so the
  // browser Back button moved the address bar and left the controls where they
  // were: a filtered list under a URL claiming otherwise, in the one feature
  // whose whole purpose (#1217) was that the view is a link you can send.
  // Deriving instead of syncing means the two cannot disagree — there is no
  // second copy to fall behind, and no effect-driven flash of the old value.
  const filterParam = searchParams.get('tl_filter')
  const filter: FilterKey = isFilterKey(filterParam) ? filterParam : 'all'
  const query = searchParams.get('tl_q') ?? ''
  const newestFirst = searchParams.get('tl_sort') !== 'oldest'
  const person = searchParams.get('tl_who') ?? ''
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [showAll, setShowAll] = useState(false)

  /**
   * Write view state to the query string, KEEPING the fragment.
   *
   * `setSearchParams` navigates with no `hash`, which drops it — and arriving
   * on a `#tl-…` permalink calls this immediately (the reveal effect clears the
   * filters), so the anchor erased itself on landing: the highlight vanished
   * and the URL was no longer the link that had just been shared. `navigate`
   * with an explicit hash is the only form that preserves it.
   */
  function syncParams(changes: Record<string, string | null>) {
    const p = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') p.delete(k)
      else p.set(k, v)
    }
    navigate(
      { pathname: location.pathname, search: p.toString(), hash: location.hash },
      { replace: true, state: location.state },
    )
  }

  const mentionNames = useMemo(() => mentionableUsers.map((u) => u.name), [mentionableUsers])

  // Base list: every event, newest-first.
  const entries = useMemo<TimelineEntry[]>(() => {
    const out: TimelineEntry[] = []
    for (const h of history ?? []) out.push(toStageEntry(h))
    for (const c of comments ?? []) out.push(toCommentEntry(c))
    for (const a of fieldAudit ?? []) out.push(toFieldEntry(a))
    for (const e of engagement ?? []) out.push(toEngagementEntry(e))
    out.sort((a, b) => (a.ts < b.ts ? 1 : a.ts > b.ts ? -1 : 0))
    return out
  }, [history, comments, fieldAudit, engagement])

  const counts = useMemo(() => {
    const c: Record<TimelineKind, number> = { comment: 0, stage: 0, field: 0, engagement: 0 }
    for (const e of entries) c[e.kind] += 1
    return c
  }, [entries])

  const people = useMemo(
    () => [...new Set(entries.map((e) => e.who))].sort((a, b) => a.localeCompare(b)),
    [entries],
  )

  const filtered = useMemo(() => {
    let list = filter === 'all' ? entries : entries.filter((e) => e.kind === filter)
    if (person) list = list.filter((e) => e.who === person)
    const q = query.trim().toLowerCase()
    if (q) {
      list = list.filter(
        (e) => e.body.toLowerCase().includes(q) || e.who.toLowerCase().includes(q),
      )
    }
    if (!newestFirst) list = [...list].reverse()
    return list
  }, [entries, filter, person, query, newestFirst])

  // Collapse runs of ≥2 field edits into one expandable group — but only in the
  // unfiltered, unsearched view (when you ask for field edits, a person, or
  // search, show them all).
  const collapse = filter === 'all' && query.trim() === '' && !person
  const rows = useMemo<Row[]>(() => {
    if (!collapse) return filtered.map((e) => ({ type: 'entry', entry: e }))
    const out: Row[] = []
    let i = 0
    while (i < filtered.length) {
      const e = filtered[i]
      if (!e) break
      if (e.kind === 'field') {
        let j = i
        while (j < filtered.length && filtered[j]?.kind === 'field') j += 1
        const members = filtered.slice(i, j)
        if (members.length >= 2) {
          out.push({ type: 'field-group', id: `fg-${members[0]!.id}`, members })
        } else {
          out.push({ type: 'entry', entry: members[0]! })
        }
        i = j
      } else {
        out.push({ type: 'entry', entry: e })
        i += 1
      }
    }
    return out
  }, [filtered, collapse])

  // Reset progressive reveal whenever the view changes.
  useEffect(() => setShowAll(false), [filter, person, query, newestFirst])

  const visibleRows = showAll ? rows : rows.slice(0, REVEAL_LIMIT)
  const hiddenCount = rows.length - visibleRows.length

  // ── Entry anchors ─────────────────────────────────────────────────────────
  // `#tl-<entry id>` reveals the full stream, expands the containing field-edit
  // group, scrolls to the entry and outlines it. The substrate for deep-linking
  // notifications to the exact event.
  const targetId = location.hash.startsWith('#tl-') ? location.hash.slice(4) : null
  const revealedFor = useRef<string | null>(null)
  const scrolledFor = useRef<string | null>(null)

  useEffect(() => {
    if (!targetId || loading || revealedFor.current === targetId) return
    revealedFor.current = targetId
    setShowAll(true)
    syncParams({ tl_filter: null, tl_q: null, tl_who: null, tl_sort: null })
    if (targetId.startsWith('field-')) {
      // The target may be inside a collapsed field-edit run; the group's id is
      // keyed on the run's first (newest) member, so walk back to it.
      const idx = entries.findIndex((e) => e.id === targetId)
      if (idx !== -1) {
        let start = idx
        while (start > 0 && entries[start - 1]!.kind === 'field') start -= 1
        const groupId = `fg-${entries[start]!.id}`
        setExpandedGroups((prev) => new Set(prev).add(groupId))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, loading, entries])

  useEffect(() => {
    if (!targetId || loading || scrolledFor.current === targetId) return
    const el = document.getElementById(`tl-${targetId}`)
    if (el) {
      scrolledFor.current = targetId
      el.scrollIntoView({ block: 'center' })
    }
  }, [targetId, loading, rows, showAll])

  function toggleGroup(id: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handlePost() {
    const trimmed = body.trim()
    if (!trimmed) return
    createComment.mutate(
      { demandId: demand.id, body: { body: trimmed, mentioned_user_ids: mentionedIds } },
      {
        onSuccess: () => {
          setBody('')
          setMentionedIds([])
          toast.showToast({ title: 'Comment posted' })
        },
        onError: (err) =>
          toast.showToast({ title: 'Failed to post comment', body: apiErrorMessage(err), intent: 'error' }),
      },
    )
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handlePost()
    }
  }

  const chips: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: entries.length },
    ...KIND_ORDER.filter((k) => counts[k] > 0).map((k) => ({
      key: k,
      label: KIND_META[k].label,
      count: counts[k],
    })),
  ]

  function renderWho(e: TimelineEntry, className?: string) {
    return (
      <span
        className={className}
        style={e.whoId ? { cursor: 'pointer', color: bayerColors.navyMid } : undefined}
        role={e.whoId ? 'link' : undefined}
        {...(e.whoId ? activatable(() => navigate(`/talents/${e.whoId}`)) : {})}
      >
        {e.who}
      </span>
    )
  }

  function renderTime(e: TimelineEntry, prefix = '') {
    // The timestamp doubles as the entry's permalink — GitHub's pattern.
    return (
      <span className={styles.meta} title={new Date(e.ts).toLocaleString()}>
        {prefix}
        <a className={styles.timeLink} href={`#tl-${e.id}`}>
          {formatRelative(e.ts)}
        </a>
      </span>
    )
  }

  function renderComment(e: TimelineEntry) {
    return (
      <div
        key={e.id}
        id={`tl-${e.id}`}
        className={mergeClasses(styles.commentCard, targetId === e.id && styles.targetEntry)}
        data-testid="timeline-comment"
      >
        <div className={styles.commentHead}>
          <UserAvatar name={e.who} size={28} />
          {renderWho(e, styles.commentAuthor)}
          {renderTime(e)}
          {isAdmin && !e.deleted && (
            <Button
              appearance="subtle"
              size="small"
              icon={<Delete16Regular />}
              title="Remove comment (admin)"
              aria-label="Remove comment"
              style={{ marginLeft: 'auto' }}
              onClick={() => {
                if (
                  window.confirm(
                    'Remove this comment for everyone? The text is destroyed; a "[deleted]" marker remains.',
                  )
                ) {
                  deleteComment.mutate({
                    demandId: demand.id,
                    commentId: Number(e.id.replace('comment-', '')),
                  })
                }
              }}
              data-testid={`remove-${e.id}`}
            />
          )}
        </div>
        <div className={styles.commentBody}>
          <TicketMarkdown>{highlightMentions(e.body, mentionNames)}</TicketMarkdown>
        </div>
      </div>
    )
  }

  function renderEvent(e: TimelineEntry) {
    const meta = KIND_META[e.kind]
    const warnId = `warn-${e.id}`
    const warnOpen = expandedGroups.has(warnId)
    return (
      <div
        key={e.id}
        id={`tl-${e.id}`}
        className={mergeClasses(styles.eventItem, targetId === e.id && styles.targetEntry)}
        data-testid={`timeline-${e.kind}`}
      >
        <span
          className={styles.iconBadge}
          style={{
            color: meta.color,
            background: `color-mix(in srgb, ${meta.color} 13%, ${tokens.colorNeutralBackground1})`,
          }}
          aria-hidden="true"
        >
          {meta.icon}
        </span>
        <div className={styles.eventLine}>
          {renderWho(e, styles.who)} <span className={styles.eventBody}>{e.body}</span>{' '}
          {renderTime(e, '· ')}
          {e.warnings && e.warnings.length > 0 && (
            <>
              <br />
              <button
                type="button"
                className={styles.warnBadge}
                onClick={() => toggleGroup(warnId)}
                aria-expanded={warnOpen}
                data-testid={`timeline-warnings-${e.id}`}
              >
                <Warning16Regular />
                {e.warnings.length} warning{e.warnings.length > 1 ? 's' : ''} acknowledged
                {warnOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
              </button>
              {warnOpen && (
                <div className={styles.warnList} data-testid={`timeline-warnings-list-${e.id}`}>
                  {e.warnings.map((w) => (
                    <span key={w.code}>
                      {w.stage
                        ? `${STAGE_LABEL[w.stage as keyof typeof STAGE_LABEL] ?? w.stage} expected: `
                        : ''}
                      {w.label}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  function renderRow(r: Row) {
    if (r.type === 'entry') {
      return r.entry.kind === 'comment' ? renderComment(r.entry) : renderEvent(r.entry)
    }
    const expanded = expandedGroups.has(r.id)
    const first = r.members[0]!
    return (
      <div key={r.id} data-testid="timeline-field-group">
        <button
          type="button"
          className={styles.groupToggle}
          onClick={() => toggleGroup(r.id)}
          aria-expanded={expanded}
          data-testid={`timeline-field-group-toggle-${r.id}`}
        >
          {expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
          <span
            className={styles.iconBadge}
            style={{
              color: KIND_META.field.color,
              background: `color-mix(in srgb, ${KIND_META.field.color} 13%, ${tokens.colorNeutralBackground1})`,
            }}
            aria-hidden="true"
          >
            {KIND_META.field.icon}
          </span>
          <span style={{ fontWeight: 600, fontSize: '13px' }}>
            {r.members.length} field changes
          </span>
          <Caption1 title={new Date(first.ts).toLocaleString()}>
            {formatRelative(first.ts)}
          </Caption1>
        </button>
        {expanded && <div className={styles.groupMembers}>{r.members.map(renderEvent)}</div>}
      </div>
    )
  }

  // Interleave date headers wherever the calendar day changes between rows.
  function renderStream(rowsToRender: Row[]): ReactElement[] {
    const out: ReactElement[] = []
    let lastDay = ''
    for (const r of rowsToRender) {
      const ts = r.type === 'entry' ? r.entry.ts : r.members[0]!.ts
      const day = new Date(ts).toDateString()
      if (day !== lastDay) {
        out.push(
          <div key={`day-${ts}`} className={styles.dateHeader} data-testid="timeline-date-header">
            {dateLabel(ts)}
          </div>,
        )
        lastDay = day
      }
      out.push(renderRow(r))
    }
    return out
  }

  return (
    <section className={styles.section} data-testid="timeline-tab">
      <div className={styles.head}>
        <h3 className={styles.title}>Timeline &amp; discussion</h3>
        <DemandEmailsButton demandId={demand.id} />
        {entries.length > 1 && (
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowSort16Regular />}
            onClick={() => {
              const next = !newestFirst
              syncParams({ tl_sort: next ? null : 'oldest' })
            }}
            data-testid="timeline-sort-toggle"
          >
            {newestFirst ? 'Newest first' : 'Oldest first'}
          </Button>
        )}
      </div>

      <div className={styles.composer}>
        <MentionTextarea
          value={body}
          onChange={setBody}
          onKeyDown={handleKey}
          mentionableUsers={mentionableUsers}
          mentionedIds={mentionedIds}
          onMentionedIdsChange={setMentionedIds}
          rows={4}
          placeholder="Add a comment or @mention someone… Markdown is supported. Comments are permanent and visible to all participants — they cannot be edited. An admin can remove a comment posted in error."
          ariaLabel="Add a comment"
        />
        <div className={styles.composerActions}>
          <Caption1>
            <span className={styles.key}>Ctrl</span> +{' '}
            <span className={styles.key}>Enter</span> to send
          </Caption1>
          <Button
            appearance="primary"
            icon={<Send16Regular />}
            disabled={!body.trim() || createComment.isPending}
            onClick={handlePost}
            data-testid="post-comment"
          >
            Post comment
          </Button>
        </div>
      </div>

      {/* Partial failure with data is the quieter half of the same fault: the
          stream renders, looks complete, and is missing a whole category of
          events. Say so rather than letting the reader treat it as the record. */}
      {!loading && partialFailure && entries.length > 0 && (
        <Caption1 role="alert" data-testid="timeline-partial">
          Some of this demand&apos;s history could not be loaded — what you see below is incomplete.
        </Caption1>
      )}

      {!loading && entries.length > 0 && (
        <div className={styles.toolbar}>
          <div className={styles.chips}>
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                className={mergeClasses(styles.chip, filter === c.key && styles.chipActive)}
                onClick={() => {
                  syncParams({ tl_filter: c.key === 'all' ? null : c.key })
                }}
                aria-pressed={filter === c.key}
                data-testid={`timeline-filter-${c.key}`}
              >
                {c.label}
                <span className={styles.chipCount}>{c.count}</span>
              </button>
            ))}
          </div>
          <Input
            className={styles.search}
            size="small"
            placeholder="Search timeline…"
            value={query}
            onChange={(_, d) => {
              syncParams({ tl_q: d.value || null })
            }}
            contentBefore={<Search16Regular />}
            aria-label="Search timeline"
            data-testid="timeline-search"
          />
          {people.length > 1 && (
            <Dropdown
              className={styles.personFilter}
              size="small"
              value={person || 'Everyone'}
              selectedOptions={[person]}
              onOptionSelect={(_, d) => {
                const next = d.optionValue ?? ''
                syncParams({ tl_who: next || null })
              }}
              aria-label="Filter by person"
              data-testid="timeline-person-filter"
            >
              <Option value="">Everyone</Option>
              {people.map((p) => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Dropdown>
          )}
        </div>
      )}

      {loading ? (
        <Spinner label="Loading timeline…" />
      ) : entries.length === 0 ? (
        partialFailure ? (
          <Caption1 role="alert" data-testid="timeline-error">
            Couldn&apos;t load this demand&apos;s history.
          </Caption1>
        ) : (
          <Caption1>No activity yet.</Caption1>
        )
      ) : rows.length === 0 ? (
        <div className={styles.emptyFiltered} data-testid="timeline-no-match">
          No matching activity.
        </div>
      ) : (
        <div className={styles.list}>
          {renderStream(visibleRows)}
          {hiddenCount > 0 && (
            <Button
              appearance="subtle"
              size="small"
              className={styles.showMore}
              onClick={() => setShowAll(true)}
              data-testid="timeline-show-more"
            >
              Show earlier activity ({hiddenCount})
            </Button>
          )}
        </div>
      )}
    </section>
  )
}

function toStageEntry(h: DemandStageHistoryEntry): TimelineEntry {
  const fromLabel = h.from_stage ? (STAGE_LABEL[h.from_stage as keyof typeof STAGE_LABEL] ?? h.from_stage.replace(/_/g, ' ')) : ''
  const toLabel = STAGE_LABEL[h.to_stage as keyof typeof STAGE_LABEL] ?? h.to_stage.replace(/_/g, ' ')
  return {
    id: `stage-${h.id}`,
    ts: h.changed_at,
    kind: 'stage',
    who: h.changed_by_name ?? 'System',
    whoId: h.changed_by,
    body: `${fromLabel ? `${fromLabel} → ` : ''}${toLabel}${h.notes ? ` — ${h.notes}` : ''}`,
    // null (predates the feature) and [] (clean move) both mean: no badge.
    warnings: h.acknowledged_warnings?.length ? h.acknowledged_warnings : undefined,
  }
}

function toCommentEntry(c: Comment): TimelineEntry {
  return {
    id: `comment-${c.id}`,
    ts: c.created_at,
    kind: 'comment',
    who: c.author_name ?? 'Unknown',
    whoId: c.author_id,
    body: c.body,
    deleted: c.is_deleted,
  }
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  caretaker_name: 'Caretaker',
  caretaker_id: 'Caretaker',
  requestor_name: 'Requestor',
  requestor_email: 'Requestor email',
  description: 'Description',
  focus_area_code: 'Focus area',
  disposition: 'Routed to',
  landing_confirmed_at: 'Landing confirmation',
  dsai_cluster: 'DSAI cluster',
  applications_closed_at: 'Applications',
  application_closes_at: 'Application deadline',
}

/** Stored values a reader should not have to decode. */
function fieldValue(field: string, raw: string | null | undefined): string {
  if (field === 'disposition') {
    if (!raw) return 'us'
    return raw === 'digital_council' ? 'DSAI' : raw
  }
  // A timestamp with microseconds says nothing the entry's own date doesn't.
  if (field === 'landing_confirmed_at') return raw ? 'confirmed' : 'cleared'
  // Closed by hand (council or the caretaker); NULL = open.
  if (field === 'applications_closed_at') return raw ? 'closed' : 'open'
  return raw || '(empty)'
}

function toFieldEntry(a: DemandFieldAuditEntry): TimelineEntry {
  const label = FIELD_LABELS[a.field_name] ?? a.field_name
  const oldVal = fieldValue(a.field_name, a.old_value)
  const newVal = fieldValue(a.field_name, a.new_value)
  // The reason is the point of a routing entry — the banner on a routed demand
  // sends people here for it, and it used to be dropped on the way (#1364).
  const why = a.reason ? ` · “${a.reason}”` : ''
  return {
    id: `field-${a.id}`,
    ts: a.changed_at,
    kind: 'field',
    who: a.changed_by_name ?? 'System',
    whoId: a.changed_by,
    body: `${label}: ${oldVal} → ${newVal}${why}`,
  }
}

const ENGAGEMENT_LABELS: Record<string, string> = {
  invitation_sent: 'invited',
  invitation_accepted: 'accepted the invitation',
  invitation_declined: 'declined the invitation',
  application_received: 'applied',
}

function toEngagementEntry(e: EngagementEvent): TimelineEntry {
  const action = ENGAGEMENT_LABELS[e.kind] ?? e.kind
  const targetPart = e.target ? ` — ${e.target}` : ''
  return {
    id: `eng-${e.id}`,
    ts: e.ts,
    kind: 'engagement',
    who: e.who,
    whoId: e.who_id,
    body: `${action}${targetPart}${e.detail ? ` · "${e.detail}"` : ''}`,
  }
}
